import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowRight, Lock } from "lucide-react";
import api from "@/lib/api";

export default function LoginPage() {
  const { user, login, error } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState({ app_name: "HPM Integrasi", app_tagline: "Sistem Penomoran Surat", app_logo: null, login_hero: null });

  useEffect(() => {
    api.get("/settings/branding").then(r => setBrand(r.data)).catch(()=>{});
  }, []);

  if (user) return <Navigate to="/dashboard" replace />;

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    const ok = await login(username.trim(), password);
    setLoading(false);
    if (ok) toast.success("Berhasil masuk");
  }

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2 bg-background" data-testid="login-page">
      {/* Left – brand */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 text-white overflow-hidden">
        {brand.login_hero ? (
          <img src={brand.login_hero} alt={brand.app_name} className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-ink-900 via-slate-800 to-brand-700" />
        )}
        <div className="absolute inset-0 bg-gradient-to-br from-ink-900/85 via-ink-900/60 to-brand/30" />
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            {brand.app_logo ? (
              <img src={brand.app_logo} alt="logo" className="h-10 w-10 rounded-md object-contain bg-white" />
            ) : (
              <div className="grid h-10 w-10 place-items-center rounded-md bg-white text-ink-900 font-heading font-bold">{(brand.app_name || "H").charAt(0).toUpperCase()}</div>
            )}
            <div>
              <div className="font-heading text-xl font-bold tracking-tight">{brand.app_name || "HPM Integrasi"}</div>
              <div className="text-xs uppercase tracking-[0.25em] text-white/70">{brand.app_tagline || "Sistem Penomoran Surat"}</div>
            </div>
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          <p className="overline mb-4 text-white/60">v1.0 · 2026</p>
          <h1 className="font-heading text-5xl font-bold leading-[1.05] tracking-tight">
            Setiap surat, satu nomor, tanpa keliru.
          </h1>
          <p className="mt-6 text-base leading-relaxed text-white/80">
            Kelola penerbitan SPH dan Invoice secara otomatis dengan kontrol penuh — multi-user,
            audit log, dan ekspor Excel kapan saja.
          </p>
        </div>

        <div className="relative z-10 flex items-center gap-6 text-xs text-white/60">
          <div className="flex items-center gap-2"><Lock className="h-3.5 w-3.5" />Terenkripsi</div>
          <div>·</div>
          <div>Database terpusat</div>
        </div>
      </div>

      {/* Right – form */}
      <div className="flex items-center justify-center p-6 sm:p-12 hpm-grid-bg">
        <div className="w-full max-w-md animate-fade-up">
          <div className="mb-10 lg:hidden flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-md bg-ink-900 text-white font-heading font-bold">H</div>
            <div className="font-heading text-xl font-bold tracking-tight">HPM Integrasi</div>
          </div>

          <p className="overline">Selamat datang kembali</p>
          <h2 className="mt-3 font-heading text-4xl font-bold tracking-tight text-ink-900">Masuk ke akun</h2>
          <p className="mt-3 text-sm text-ink-500">Gunakan kredensial yang diberikan oleh administrator.</p>

          <Card className="mt-8 border-border bg-card p-8 shadow-none">
            <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  data-testid="login-username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Contoh: HPM"
                  required
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>

              {error ? (
                <div
                  className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
                  data-testid="login-error"
                >
                  {error}
                </div>
              ) : null}

              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit-button"
                className="group w-full bg-ink-900 text-white hover:bg-brand hover:text-white transition-colors duration-200"
              >
                {loading ? "Memproses…" : "Masuk"}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </form>
          </Card>

          <p className="mt-6 text-xs text-ink-500">
            Belum punya akun? Hubungi administrator perusahaan Anda.
          </p>
        </div>
      </div>
    </div>
  );
}
